// React and Hooks
import { useMutation } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Actions
import {
  actionSetOperatorIsLoggingIn,
  actionSetRecentOperatorRuns,
} from 'lib/actions';
// Selectors
import { getOperatorIsLoggingIn } from 'lib/selectors/getOperatorIsLoggingIn';
// Utils
import { START_OPERATOR } from 'lib/api/mutations';
import { getIsLaborTicketEnabled } from 'lib/selectors/getIsLaborTicketEnabled';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { getLoadingCurrentWorkOrderOp } from 'lib/selectors/getLoadingCurrentWorkOrderOp';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import useStartOperatorWithLaborTicket from './useStartOperatorWithLaborTicket';

// Components

/**
 * Hook to manage operator login process using GraphQL mutation
 * @returns {Function} A function that starts an operator session:
 *   - Returns null if another operator is currently logging in
 *   - Otherwise returns a Promise resolving to the mutation result
 *
 * @example
 * const startOperator = useStartOperator();
 * const result = await startOperator({
 *   variables: {
 *     input: {
 *       machineId: '123',
 *       operatorId: '456',
 *       startAt: '2023-01-01T00:00:00Z'
 *     }
 *   }
 * });
 */
const useStartOperator = () => {
  const dispatch = useDispatch();
  const isLoggingIn = useSelector(getOperatorIsLoggingIn);
  const laborTicketEnabled = useSelector(getIsLaborTicketEnabled);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const loadingCurrentWorkOrderOp = useSelector(getLoadingCurrentWorkOrderOp);
  const isPaused = useSelector(getIsPaused);
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);

  const startOperatorWithLaborTicket = useStartOperatorWithLaborTicket();

  const [startOperator, { loading }] = useMutation(START_OPERATOR, {
    fetchPolicy: 'no-cache',
    onCompleted: ({ startOperatorResponse }) => {
      if (startOperatorResponse) {
        const { machine, operator, ...operatorRun } = startOperatorResponse;
        const mappedPayload = {
          operatorRuns: [
            {
              ...operatorRun,
              machineId: machine?.id,
              operatorId: operator?.id,
            },
          ],
        };
        dispatch(actionSetRecentOperatorRuns(mappedPayload));
      }
    },
  });

  useEffect(() => {
    dispatch(actionSetOperatorIsLoggingIn(loading));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const startOperatorStateRef = useRef({});
  startOperatorStateRef.current = {
    isLoggingIn,
    loading,
    laborTicketEnabled,
    loadingCurrentWorkOrderOp,
    currentWorkOrderOp,
    isPaused,
    isApmERPJobFlow,
  };

  /**
   * @param {Object} args - The mutation arguments
   * @param {Object} args.variables - The variables for the START_OPERATOR mutation
   * @param {Object} args.variables.input - Input object for starting an operator
   * @param {string} args.variables.input.machineId - The ID of the machine
   * @param {string} args.variables.input.operatorId - The ID of the operator
   * @param {boolean} [args.variables.input.pinToPreviousEvent] - Optional flag to pin to previous event
   * @param {string} [args.variables.input.startAt] - Optional ISO timestamp for start time
   * @returns {Promise<{data?: {startOperatorResponse?: Object}, errors?: Array<import('@apollo/client').ApolloError>}>}
   */
  return async (startOperatorRun) => {
    const {
      isLoggingIn: loggingIn,
      loading: startLoading,
      laborTicketEnabled: laborTicketsOn,
      loadingCurrentWorkOrderOp: workOrderOpLoading,
      currentWorkOrderOp: workOrderOp,
      isPaused: paused,
      isApmERPJobFlow: apmErpFlow,
    } = startOperatorStateRef.current;

    if (loggingIn || startLoading) {
      return null;
    }

    // If labor tickets are enabled, wait for currentWorkOrderOp to finish loading
    // to avoid race condition where we call legacy startOperator instead of startOperatorWithLaborTicket
    if (laborTicketsOn && workOrderOpLoading && !apmErpFlow) {
      return null;
    }

    // APM ERP: program events populate currentWorkOrderOp for UI only — sign-in must not
    // create/open a labor ticket; operators confirm events separately.
    if (laborTicketsOn && workOrderOp && !paused && !apmErpFlow) {
      return startOperatorWithLaborTicket(startOperatorRun);
    }

    return startOperator({
      variables: {
        input: startOperatorRun,
      },
    });
  };
};

export default useStartOperator;
